"use client";

import { fetchCategoriesBySlug } from "@/api-service/category";
import ProductGrid from "@/components/gridPage/ProductGrid";
import Sidebar from "@/components/gridPage/sidebar/SideBar";
import { Category } from "@/packages/types/category";
import { useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useState, Suspense } from "react";
import { toast } from "sonner";
import { fetchGridProducts } from "@/api-service/product";
import { Product } from "@/packages/types/products";
import SelectField from "@/components/common/SelectBox";
import { Button } from "@/components/ui/button";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "@/components/common/NextImage";

interface CategoryPageProps {
  params: Promise<{ slug: string }>;
}

function CategoryPageContent({ params }: CategoryPageProps) {
  const { slug } = use(params);
  const { fastPush } = useFastRouter();
  const searchParams = useSearchParams();

  /* ---------------- URL Derived Values ---------------- */
  const page = Number(searchParams.get("page")) || 1;

  const category = useMemo(() => {
    const cats = searchParams.get("categories");
    return cats ? cats.split(",").map(Number) : [];
  }, [searchParams]);

  const startPriceParam = searchParams.get("startPrice");
  const endPriceParam = searchParams.get("endPrice");
  const startPrice = startPriceParam ? Number(startPriceParam) : undefined;
  const endPrice = endPriceParam ? Number(endPriceParam) : undefined;

  const sortFromUrl = searchParams.get("sort") || "";

  /* ---------------- State ---------------- */
  const [categoryData, setCategoryData] = useState<Category>();
  const [products, setProducts] = useState<Product[]>();
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [sort, setSort] = useState<string>(sortFromUrl);
  const [bannerError, setBannerError] = useState<boolean>(false);

  /* ---------------- Responsive Limit ---------------- */
  useEffect(() => {
    const updateLimit = () => {
      setLimit(window.innerWidth <= 768 ? 16 : 20);
    };

    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  /* ---------------- Fetch Category ---------------- */
  useEffect(() => {
    setBannerError(false);
    const fetchData = async () => {
      try {
        const res = await fetchCategoriesBySlug(slug);
        setCategoryData(res);

        if (res?.data?.minPrice) setMinPrice(res.data.minPrice);
        if (res?.data?.maxPrice) setMaxPrice(res.data.maxPrice);
      } catch (error: unknown) {
        console.error(error);
        toast.error("Something went wrong");
      }
    };

    fetchData();
  }, [slug, fastPush, searchParams]);

  /* ---------------- Fetch Products ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Build categories for API: always include the main category id (from slug)
        // plus any selected categories from the URL (e.g., child categories).
        const mainCatId = categoryData?.data?.id;

        const categoriesForApi =
          category.length > 0 ? category : mainCatId ? [mainCatId] : [];

        if (categoriesForApi.length > 0) {
          const res = await fetchGridProducts(
            categoriesForApi,
            page,
            limit,
            startPrice,
            endPrice,
            sort
          );

          setProducts(res.data.products);
          setTotalPages(res.data.totalPages ?? 1);
        }
      } catch (error: unknown) {
        console.error(error);
        toast.error("Something went wrong");
      }
    };

    fetchData();
  }, [category, page, startPrice, endPrice, sort, limit, categoryData]);

  /* ---------------- Pagination ---------------- */
  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(newPage));
    fastPush(`/collections/${slug}?${params.toString()}`);
  };

  /* ---------------- Sidebar Visibility ---------------- */
  const showSidebar = false;

  const categoryBannerUrl = categoryData?.data?.bannerUrl?.trim();
  const categoryName = categoryData?.data?.name;

  /* ---------------- JSX ---------------- */
  return (
    <div className="max-w-7xl mx-auto mt-5 px-5 md:px-0">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        {showSidebar && (
          <div className="hidden md:block md:w-1/4">
            <Sidebar
              category={categoryData}
              minPrice={minPrice}
              maxPrice={maxPrice}
            />
          </div>
        )}

        {/* Main Content */}
        <div className={`w-full ${showSidebar ? "md:w-3/4" : "md:w-full"}`}>
          {/* Banner (Standard 4:1 Aspect Ratio) */}
          {categoryBannerUrl && !bannerError && (
            <div className="w-full mb-6">
              <div className="relative w-full aspect-[4/1] overflow-hidden rounded-xl bg-slate-100 border border-slate-200/60 shadow-xs">
                <NextImage
                  src={categoryBannerUrl}
                  alt={categoryName ? `${categoryName} Banner` : "Category Banner"}
                  fill
                  sizes="(max-width: 768px) 100vw, 1280px"
                  className="object-cover transition-transform duration-700 ease-out hover:scale-102"
                  priority
                  onError={() => setBannerError(true)}
                />
              </div>
            </div>
          )}

          {/* Header Row */}
          <div className="flex items-center justify-between gap-4 mt-6 mb-2">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight capitalize">
              {categoryName || "Collection"}
            </h1>
            <div className="w-60">
              <SelectField
                name="sort"
                placeholder="Sort by"
                value={sort}
                onChange={(val) => {
                  setSort(val);
                  const params = new URLSearchParams(searchParams.toString());

                  if (val) params.set("sort", val);
                  else params.delete("sort");

                  params.set("page", "1");
                  fastPush(`/collections/${slug}?${params.toString()}`);
                }}
                options={[
                  { value: "new", label: "New" },
                  { value: "a_to_z", label: "A to Z" },
                  { value: "z_to_a", label: "Z to A" },
                  { value: "price_low_to_high", label: "Price Low to High" },
                  { value: "price_high_to_low", label: "Price High to Low" },
                  { value: "top_selling", label: "Top Selling" },
                ]}
              />
            </div>
          </div>

          {/* Products */}
          {products?.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center">
              <h2 className="text-lg font-semibold">No Products Found</h2>
              <p className="text-sm text-gray-500">
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <ProductGrid products={products} />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-20 mb-10 flex justify-center gap-2">
              <Button
                disabled={page === 1}
                onClick={() => goToPage(page - 1)}
                className="bg-gray-200 text-black"
              >
                Previous
              </Button>

              <span className="px-4 py-2 text-sm">
                Page {page} of {totalPages}
              </span>

              <Button
                disabled={page === totalPages}
                onClick={() => goToPage(page + 1)}
                className="bg-gray-200 text-black"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CategoryPage({ params }: CategoryPageProps) {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading collection...</div>}>
      <CategoryPageContent params={params} />
    </Suspense>
  );
}
